/**
 * usePersonProfile - Fetches person's evidence and behavioral codes.
 *
 * Calls api.searchBehavioralDocuments() with person hash filter,
 * then aggregates codes by dimension for the RadarChart.
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
// NOTE: Platform constants - consider extracting to shared/constants/
import { FAANA_DIMENSIONS } from '../../BehavioralWorkbench/constants/workbenchConfig';

function usePersonProfile(entityId, personHash) {
  const [state, setState] = useState({
    dimensionScores: {},
    topCodes: [],
    evidence: [],
    loading: false,
    error: null,
  });

  const fetchData = useCallback(async () => {
    if (!entityId || !personHash) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Search for chunks mentioning this person via vector similarity search.
      // Uses the person hash/label as query text since the SNA search endpoint
      // doesn't support person hash filtering directly.
      const result = await api.querySNAVectorStore({
        query: personHash,
        entityId,
        limit: 100,
        includeContent: true,
      });

      const chunks = result?.results || result?.chunks || [];

      // Aggregate codes by dimension
      const codeMap = {};
      const dimensionCounts = {};
      FAANA_DIMENSIONS.forEach((dim) => {
        dimensionCounts[dim] = 0;
      });

      chunks.forEach((chunk) => {
        const codes = chunk.codes || chunk.behavioral_codes || [];
        codes.forEach((code) => {
          const codeName = typeof code === 'string' ? code : code.name || code.code;
          const dimension = typeof code === 'object' ? code.dimension : null;
          const valence = typeof code === 'object' ? code.valence : null;

          if (!codeMap[codeName]) {
            codeMap[codeName] = { name: codeName, count: 0, dimension, valence };
          }
          codeMap[codeName].count++;

          if (dimension && dimensionCounts[dimension] !== undefined) {
            dimensionCounts[dimension]++;
          }
        });
      });

      // Normalize dimension counts to 0-100 scale for RadarChart
      const maxDimCount = Math.max(...Object.values(dimensionCounts), 1);
      const dimensionScores = {};
      FAANA_DIMENSIONS.forEach((dim) => {
        dimensionScores[dim] = Math.round((dimensionCounts[dim] / maxDimCount) * 100);
      });

      // Sort codes by count
      const topCodes = Object.values(codeMap).sort((a, b) => b.count - a.count);

      setState({
        dimensionScores,
        topCodes,
        evidence: chunks,
        loading: false,
        error: null,
      });
    } catch (err) {
      console.error('usePersonProfile error:', err);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err,
      }));
    }
  }, [entityId, personHash]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    ...state,
    retry: fetchData,
  };
}

export default usePersonProfile;
